import React from 'react';
import { FaInfoCircle } from 'react-icons/fa';
import { UNIDADES_AYUDA_MSG } from '../../utils/precioSugerido';

const fieldClass = 'nexo-field mt-1';

/**
 * Panel de gastos mensuales + explicación para sugerencia de precio (tema 0E3).
 */
export default function PricingSuggestionPanel({
  pricingSuggestionConfig,
  onConfigChange,
  sugerencia,
  onSyncFinanzas,
  syncingFinanzas = false,
  compact = false
}) {
  const cfg = pricingSuggestionConfig || {};

  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-e0e3-accent/25 bg-e0e3-accent/10 px-3 py-3">
        <div className="flex gap-2">
          <FaInfoCircle className="mt-0.5 h-4 w-4 shrink-0 text-e0e3-accent-soft" aria-hidden />
          <div className="text-sm text-e0e3-foreground">
            <p className="font-semibold text-e0e3-accent-soft">¿Cómo calcula el sistema?</p>
            <p className="mt-1 text-xs leading-relaxed text-e0e3-muted">
              Los gastos como alquiler, luz o sueldos son <strong className="text-e0e3-foreground">mensuales</strong>.
              Para estimar cuánto impactan en este producto, indicá aproximadamente cuántas unidades vendés o
              producís por mes. El sistema calculará una <strong className="text-e0e3-foreground">sugerencia</strong> — vos
              decidís el precio final.
            </p>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-white/10 bg-white/[0.04] p-3">
        <p className="text-xs font-semibold text-e0e3-foreground">Gastos mensuales y estimación</p>
        <div className={`mt-2 grid gap-2 ${compact ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2'}`}>
          <label className="text-xs text-e0e3-muted">
            Alquiler mensual
            <input
              type="number"
              min="0"
              step="0.01"
              value={cfg.alquilerMensual ?? ''}
              onChange={(e) => onConfigChange('alquilerMensual', e.target.value)}
              className={fieldClass}
            />
          </label>
          <label className="text-xs text-e0e3-muted">
            Móvil / teléfono
            <input
              type="number"
              min="0"
              step="0.01"
              value={cfg.movilMensual ?? ''}
              onChange={(e) => onConfigChange('movilMensual', e.target.value)}
              className={fieldClass}
            />
          </label>
          <label className="text-xs text-e0e3-muted">
            Combustible
            <input
              type="number"
              min="0"
              step="0.01"
              value={cfg.combustibleMensual ?? ''}
              onChange={(e) => onConfigChange('combustibleMensual', e.target.value)}
              className={fieldClass}
            />
          </label>
          <label className="text-xs text-e0e3-muted">
            Otros gastos mensuales
            <input
              type="number"
              min="0"
              step="0.01"
              value={cfg.otrosGastosMensuales ?? ''}
              onChange={(e) => onConfigChange('otrosGastosMensuales', e.target.value)}
              className={fieldClass}
            />
          </label>
          <label className="text-xs text-e0e3-muted">
            Unidades vendidas/producidas por mes
            <input
              type="number"
              min="0"
              step="1"
              placeholder="Ej: 1000"
              value={cfg.unidadesMensualesEstimadas ?? ''}
              onChange={(e) => onConfigChange('unidadesMensualesEstimadas', e.target.value)}
              className={fieldClass}
            />
          </label>
          <label className="text-xs text-e0e3-muted">
            Margen objetivo (%)
            <input
              type="number"
              min="0"
              max="99"
              step="0.1"
              value={cfg.margenObjetivoPct ?? ''}
              onChange={(e) => onConfigChange('margenObjetivoPct', e.target.value)}
              className={fieldClass}
            />
          </label>
        </div>

        {typeof onSyncFinanzas === 'function' && (
          <button
            type="button"
            onClick={onSyncFinanzas}
            disabled={syncingFinanzas}
            className="mt-2 rounded-lg border border-e0e3-accent/30 bg-e0e3-accent/10 px-3 py-2 text-xs font-semibold text-e0e3-accent-soft hover:bg-e0e3-accent/20 disabled:opacity-60"
          >
            {syncingFinanzas ? 'Sincronizando…' : 'Usar gastos de Finanzas (últimos 30 días)'}
          </button>
        )}

        {sugerencia?.warnings?.unidadesRequeridasParaGastos && (
          <p className="mt-2 text-xs font-medium text-amber-300/90">{UNIDADES_AYUDA_MSG}</p>
        )}

        {sugerencia?.canSuggest && (
          <p className="mt-2 text-xs text-e0e3-muted">
            Gastos mensuales: <strong className="text-e0e3-foreground">${sugerencia.gastosMensuales.toFixed(2)}</strong>
            {' · '}
            Costo fijo por unidad:{' '}
            <strong className="text-e0e3-foreground">
              {sugerencia.incluyeGastosFijos ? `$${sugerencia.gastoPorUnidad.toFixed(2)}` : '— (sin prorrateo)'}
            </strong>
          </p>
        )}

        <p className="mt-1 text-[11px] text-e0e3-muted">
          Fórmula: <strong className="text-e0e3-foreground">(Costo + Gastos ÷ Unidades) ÷ (1 − Margen%)</strong>
        </p>

        {sugerencia?.warnings?.margenAjustado && (
          <p className="mt-1 text-xs font-medium text-amber-300/90">
            El margen objetivo se ajustó al rango 0–95%.
          </p>
        )}
      </div>
    </div>
  );
}
